import React, { useState } from 'react';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { REQUEST_ACCESS_URL, SIGN_IN_URL } from '../config/links';

/* Workbench chrome — serif brand, sans nav, pill CTA. Colors come from the
   global --wb-* tokens (light by default), so it reads identically on every
   marketing page, notebook-styled subpages included. Pages that support dark
   mode (the landing page) pass `theme` and `onToggleTheme` to show the flip. */

const sans = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const navLink = {
  fontFamily: sans,
  fontSize: 'var(--t-md)',
  color: 'var(--wb-ink-2)',
  fontWeight: 500,
};

const ThemeFlip = ({ theme, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
    style={{ width: 30, height: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, border: '1px solid var(--wb-line)', background: 'transparent', color: 'var(--wb-ink-2)', cursor: 'pointer', padding: 0 }}
  >
    {theme === 'dark' ? <Sun size={14} aria-hidden /> : <Moon size={14} aria-hidden />}
  </button>
);

const MarketingHeader = ({ theme, onToggleTheme }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-[100]"
      style={{
        borderBottom: '1px solid var(--wb-line)',
        background: 'var(--wb-header)',
        backdropFilter: 'saturate(140%) blur(6px)',
      }}
    >
      <div className="max-w-[1180px] mx-auto px-5 flex items-center justify-between h-16">
        {/* Brand */}
        <a href="/" className="flex items-center gap-2.5 no-underline" style={{ fontFamily: sans, fontWeight: 800, letterSpacing: '0.02em', fontSize: 'var(--t-md)', color: 'var(--wb-ink)' }}>
          <img src="/why-book.png" alt="" aria-hidden width={30} height={30} style={{ display: 'block', width: 30, height: 30, objectFit: 'contain' }} />
          FACTORY OS
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <a href="https://demo.factory-os.com" className="no-underline" style={navLink}>Take the tour</a>
          <a href="/pricing" className="no-underline" style={navLink}>Pricing</a>
          <a href="/resources" className="no-underline" style={navLink}>Resources</a>
          <a href={SIGN_IN_URL} className="no-underline" style={navLink}>Sign in</a>
          {onToggleTheme && <ThemeFlip theme={theme} onToggle={onToggleTheme} />}
          <a
            href={REQUEST_ACCESS_URL}
            className="no-underline"
            style={{ fontFamily: sans, fontSize: 'var(--t-md)', fontWeight: 600, padding: '10px 18px', background: 'var(--wb-ink)', color: 'var(--wb-on-ink)', borderRadius: 999 }}
          >
            Request access
          </a>
        </div>

        {/* Mobile: theme flip + hamburger */}
        <div className="md:hidden flex items-center gap-1">
        {onToggleTheme && <ThemeFlip theme={theme} onToggle={onToggleTheme} />}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2"
          style={{ color: 'var(--wb-ink-2)', background: 'none', border: 'none', cursor: 'pointer' }}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden px-5 py-6 space-y-4" style={{ background: 'var(--wb-bg)', borderTop: '1px solid var(--wb-line)' }}>
          <a href="https://demo.factory-os.com" className="block py-2 no-underline" style={{ fontFamily: sans, fontSize: 'var(--t-md)', fontWeight: 700, color: 'var(--wb-ink)' }}>
            Take the 2-minute tour →
          </a>
          {[
            ['Pricing', '/pricing'],
            ['Resources', '/resources'],
            ['Free notebook', '/start'],
            ['Machining program', '/machining'],
            ['Contact', '/contact'],
          ].map(([label, href]) => (
            <a key={href} href={href} className="block py-2 no-underline" style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-ink)' }}>
              {label}
            </a>
          ))}
          <div style={{ borderTop: '1px solid var(--wb-line)' }} />
          <a href={SIGN_IN_URL} className="block py-2 no-underline" style={{ ...navLink, fontSize: 'var(--t-md)' }}>
            Sign in
          </a>
          <a
            href={REQUEST_ACCESS_URL}
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full no-underline text-center"
            style={{ fontFamily: sans, fontSize: 'var(--t-md)', fontWeight: 700, padding: '13px 22px', background: 'var(--wb-ink)', color: 'var(--wb-on-ink)', borderRadius: 999 }}
          >
            Request access
          </a>
        </div>
      )}
    </header>
  );
};

export default MarketingHeader;
